import { Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, MouseEvent, useCallback, useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  useDeleteView,
  useDuplicateView,
  useResetView,
} from '../../../../../pages/PortfolioOverview/hooks/useViewActions';
import { usePortfolioConfigActions } from '../../../../../pages/AdminPanel/DataDisplay/useDataDisplayConfigActions';
import { showPromoteViewDialog } from '../../../../../pages/PortfolioOverview/promote-view/PromoteView';
import { selectedViewPFJ } from '../../../../../pages/PortfolioOverview/state/ViewStateJ';
import { ViewAction, ViewActionName } from '../../../../../types';
import { useToastMessageState } from '../../../../ToastMessage/ToastMessageProvider';
import { DeleteViewConfirmDialog } from './DeleteViewConfirmDialog';

interface Props {
  options: ViewAction[];
  openShareDialog: () => void;
  onRename: () => void;
}

const ItemWrapper = styled('div')``;
const Wrapper = styled('div')`
  margin-top: -3px;
`;

export const ViewMenu: FC<Props> = ({ options, openShareDialog, onRename }) => {
  const { colors } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleDuplicate = useDuplicateView();
  const handleReset = useResetView();
  const handleDelete = useDeleteView();
  const selectedView = useAtomValue(selectedViewPFJ);
  const { handleSave: handleSetAsLanding } = usePortfolioConfigActions();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  const closeDeleteConfirm = useCallback(() => {
    setShowConfirmDelete(false);
  }, []);

  const onDelete = useCallback(() => {
    if (!selectedView) {
      return;
    }
    handleDelete(selectedView);
  }, [handleDelete, selectedView]);

  const onConfirmDelete = useCallback(() => {
    closeDeleteConfirm();
    onDelete();
  }, [closeDeleteConfirm, onDelete]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const setShowPromoteViewDialog = useSetAtom(showPromoteViewDialog);
  const onPromote = useCallback(() => {
    setShowPromoteViewDialog(true);
  }, [setShowPromoteViewDialog]);

  const onDuplicate = useCallback(() => {
    if (!selectedView) {
      return;
    }
    handleDuplicate(selectedView);
  }, [handleDuplicate, selectedView]);

  const onReset = useCallback(() => {
    if (!selectedView) {
      return;
    }
    handleReset(selectedView);
  }, [handleReset, selectedView]);

  const onSetAsLanding = useCallback(async () => {
    if (!selectedView) {
      return;
    }
    try {
      await handleSetAsLanding(selectedView.id);
      pushSuccessToast({ message: `"${selectedView.name}" set as landing view.` });
    } catch {
      pushErrorToast({ message: 'Failed to set landing view.' });
    }
  }, [handleSetAsLanding, selectedView, pushSuccessToast, pushErrorToast]);

  const handleSelectOption = useCallback(
    (e: MouseEvent<HTMLElement>, id: ViewActionName) => {
      e.stopPropagation();
      handleCloseMenu();
      switch (id) {
        case ViewActionName.RENAME:
          onRename();
          return;
        case ViewActionName.DUPLICATE:
          onDuplicate();
          return;
        case ViewActionName.RESET:
          onReset();
          return;
        case ViewActionName.SHARE:
          openShareDialog();
          return;
        case ViewActionName.DELETE:
          setShowConfirmDelete(true);
          return;
        case ViewActionName.PROMOTE:
          onPromote();
          return;
        case ViewActionName.SET_AS_LANDING:
          onSetAsLanding();
          return;
        default:
          throw new Error('Unknown option');
      }
    },
    [onDuplicate, onRename, onReset, openShareDialog, onPromote, onSetAsLanding]
  );

  return (
    <Wrapper>
      <IconButton
        data-testid='menu-btn'
        onClick={(e) => {
          e.stopPropagation();
          handleClick(e);
        }}
      >
        <MoreVertIcon style={{ color: 'white' }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={(e: MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.stopPropagation();
          handleCloseMenu();
        }}
        disablePortal
        PaperProps={{
          style: {
            width: '180px',
            transform: 'translateX(-95px)',
          },
        }}
      >
        {options.map(({ id, hasSeparator, icon, value }) => (
          <ItemWrapper key={id}>
            {hasSeparator && <Divider />}
            <MenuItem onClick={(e) => handleSelectOption(e, id)}>
              <ListItemIcon>{icon}</ListItemIcon>
              <Typography variant='body2' color={colors.primary[100]}>
                {value}
              </Typography>
            </MenuItem>
          </ItemWrapper>
        ))}
      </Menu>
      <DeleteViewConfirmDialog
        view={selectedView}
        open={showConfirmDelete}
        onClose={closeDeleteConfirm}
        onConfirm={onConfirmDelete}
      />
    </Wrapper>
  );
};
